// Сжатие фото перед отправкой: снимок с телефона весит 3–8 МБ, а для чата и
// аватара хватает 1920 / 1024 px. Сервер всё равно пережимает в webp — здесь
// экономим трафик менеджера и укладываемся в лимит запроса.

export interface PreparedImage {
  blob: Blob;
  /** Имя с расширением — по нему сервер понимает формат. */
  name: string;
  width: number;
  height: number;
}

export interface CompressOptions {
  /** Длинная сторона после сжатия. */
  maxSide: number;
  /** Качество JPEG 0–1. */
  quality: number;
  /** Меньше этого и в нормальном формате — отправляем как есть. */
  maxBytes: number;
}

export const CHAT_PHOTO: CompressOptions = { maxSide: 1920, quality: 0.82, maxBytes: 1_200_000 };
export const AVATAR_PHOTO: CompressOptions = { maxSide: 1024, quality: 0.86, maxBytes: 400_000 };

const PASS_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
}

async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      // EXIF-поворот с телефона учитываем, иначе фото ляжет на бок.
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height, dispose: () => bmp.close() };
    } catch {
      /* старый Safari или экзотический формат — пробуем через <img> */
    }
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    dispose: () => URL.revokeObjectURL(url),
  };
}

function baseName(file: File): string {
  return (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
}

/** GIF не трогаем (анимация), остальное — в JPEG не больше maxSide, если файл большой. */
export async function prepareImage(file: File, opts: CompressOptions): Promise<PreparedImage> {
  if (file.type === 'image/gif') {
    const d = await decode(file).catch(() => null);
    d?.dispose();
    return { blob: file, name: file.name || 'photo.gif', width: d?.width ?? 0, height: d?.height ?? 0 };
  }

  const d = await decode(file);
  try {
    const scale = Math.min(1, opts.maxSide / Math.max(d.width, d.height));
    if (scale === 1 && file.size <= opts.maxBytes && PASS_TYPES.has(file.type)) {
      return { blob: file, name: file.name || 'photo.jpg', width: d.width, height: d.height };
    }
    const width = Math.max(1, Math.round(d.width * scale));
    const height = Math.max(1, Math.round(d.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d недоступен');
    // Прозрачный PNG в JPEG стал бы чёрным — подкладываем белый.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(d.source, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', opts.quality));
    // iOS долго держит память канваса — отпускаем сразу.
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error('не удалось сжать картинку');
    // Небольшой PNG-скриншот в JPEG иногда только толстеет — тогда шлём оригинал.
    if (scale === 1 && blob.size >= file.size && PASS_TYPES.has(file.type)) {
      return { blob: file, name: file.name || 'photo.png', width: d.width, height: d.height };
    }
    return { blob, name: `${baseName(file)}.jpg`, width, height };
  } finally {
    d.dispose();
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}
